import type { StateCreator } from 'zustand';
import type { StoreState, SessionState, Identifiable } from '../types/types';
import { merge, debounce } from 'lodash-es';
import { api } from '../services/api';
import { logger } from '../services/logger.client';

export interface LogEntryUser {
  username: string;
}
export interface LogEntry extends Identifiable {
  timestamp: string;
  text: string;
  userId: number | null;
  user?: LogEntryUser | null;
}

export interface GeneralState {
  activeCampaignId: number | null;
  activeSessionId: number | null;
  campWatchOrder: number[];
  worldDynamics: string;
  worldDynamicsLastSaved?: Date | null;
  campaignLog: LogEntry[];
  gameTime: Date | null;
  sessionNotes: string | null;
  scratchpadNotes: string | null;
  sessions: import('../types/types').Session[];
}
export interface GeneralActions {
  loadStateFromObject: (newState: Partial<SessionState>) => void;
  setGameTime: (newTime: string) => Promise<void>;
  updateSessionNotes: (notes: string) => Promise<void>;
  updateWatchOrder: (newOrder: number[]) => Promise<void>;
  updateScratchpadNotes: (notes: string) => Promise<void>;
  addLogEntry: (text: string) => void;
  updateWorldDynamics: (notes: string) => Promise<void>;
  updateSettings: (settings: Partial<StoreState['settings']>) => Promise<void>;
  startSession: (gameTimeStart: string) => Promise<void>;
  endSession: () => Promise<void>;
}

const debouncedSaveWorldDynamics = debounce(async (notes: string, set: (fn: (store: StoreState) => void) => void) => {
  try {
    await api('/api/app-config', { method: 'PUT', body: JSON.stringify({ worldDynamics: notes }) });
    // On successful save, update the last saved timestamp.
    set(state => { state.session.general.worldDynamicsLastSaved = new Date(); });
    logger.info('World dynamics auto-saved successfully.');
  } catch (err) {
    logger.error(err, 'Failed to auto-save world dynamics.');
    // Optionally, you could add a state field to show a save error to the user.
  }
}, 10000); // 10-second debounce delay


export type GeneralSlice = GeneralState & GeneralActions;

export const createGeneralActions: StateCreator<StoreState, [['zustand/immer', never]], [], { state: GeneralState, actions: GeneralActions }> = (set, get) => ({
  state: {
    activeCampaignId: null,
    activeSessionId: null,
    campWatchOrder: [],
    worldDynamics: '',
    worldDynamicsLastSaved: null,
    campaignLog: [],
    gameTime: null,
    sessionNotes: null,
    scratchpadNotes: null,
    sessions: [],
  },
  actions: {
    loadStateFromObject: (newState) => {
      set(store => {
        merge(store, newState);
      });
    },
    setGameTime: async (newTime: string) => {
      const originalTime = get().session.general.gameTime;
      set(state => { state.session.general.gameTime = new Date(newTime); });

      try {
        await api('/api/app-config', { method: 'PUT', body: JSON.stringify({ gameTime: newTime }) });
      } catch (err) {
        logger.error(err, 'Failed to save game time, rolling back.');
        set(state => { state.session.general.gameTime = originalTime; });
        throw err;
      }
    },
    updateSessionNotes: async (notes: string) => {
      const originalNotes = get().session.general.sessionNotes;
      set(state => { state.session.general.sessionNotes = notes; });

      try {
        await api('/api/app-config', { method: 'PUT', body: JSON.stringify({ sessionNotes: notes }) });
      } catch (err) {
        logger.error(err, 'Failed to save session notes, rolling back.');
        set(state => { state.session.general.sessionNotes = originalNotes; });
        throw err;
      }
    },
    updateWatchOrder: async (newOrder: number[]) => {
      const originalOrder = get().session.general.campWatchOrder;
      set(state => { state.session.general.campWatchOrder = newOrder; });

      try {
        await api('/api/camp-watch-order', { method: 'PUT', body: JSON.stringify({ orderedIds: newOrder }) });
      } catch (err) {
        logger.error(err, 'Failed to save watch order, rolling back.');
        set(state => { state.session.general.campWatchOrder = originalOrder; });
        throw err;
      }
    },
    updateScratchpadNotes: async (notes) => {
      // This is a special case. The scratchpad is meant to be ephemeral and auto-saving.
      // A full optimistic update with rollback might be jarring if it reverts user typing.
      // So, we'll just fire-and-forget the save, and update the local state.
      set(state => { state.session.general.scratchpadNotes = notes; });
      try {
        await api('/api/app-config', { method: 'PUT', body: JSON.stringify({ scratchpadNotes: notes }) });
      } catch (err) {
        logger.error(err, 'Failed to save scratchpad notes.');
      }
    },
    addLogEntry: (text) => {
      const timestamp = get().session.general.gameTime?.toISOString() ?? new Date().toISOString();
      const user = get().auth.user;
      const tempEntry = { timestamp, text, id: -Date.now(), userId: user?.id, user: { username: user?.username } }; // Use a temporary ID

      // Optimistically update the UI
      set(store => {
        store.session.general.campaignLog.unshift(tempEntry as unknown as LogEntry);
      });

      // Asynchronously save to the backend
      api('/api/log', { method: 'POST', body: JSON.stringify({ text, timestamp }) })
        .catch(err => {
          logger.error(err, 'Failed to save log entry to database.');
        });
    },
    updateWorldDynamics: async (notes: string) => {
        // Update the local state immediately for a responsive UI.
        set(state => { state.session.general.worldDynamics = notes; });
        // Trigger the debounced save operation.
        debouncedSaveWorldDynamics(notes, set);
    },
    updateSettings: async (settings) => {
      const originalSettings = get().settings;
      const newSettings = { ...originalSettings, ...settings };
      set(state => { state.settings = newSettings; });

      try {
        await api('/api/user/settings', { method: 'PUT', body: JSON.stringify(settings) });
      } catch (err) {
        logger.error(err, 'Failed to save settings, rolling back.');
        set(state => { state.settings = originalSettings; });
        throw err;
      }
    },
    startSession: async (gameTimeStart: string) => {
      if (get().session.general.activeSessionId) {
        logger.warn('A session is already active.');
        return;
      }
      try {
        // The API returns the full, updated state on success
        const response = await api('/api/sessions/start', { method: 'POST', body: JSON.stringify({ gameTimeStart }) });
        const latestState = await response.json() as SessionState;
        get().hydrateState(latestState);
        get().actions.addLogEntry('New session started.');
      } catch (error) {
        logger.error(error, 'Error starting session');
      }
    },
    endSession: async () => {
      const session = get().session;
      const sessionId = session.general.activeSessionId;
      if (!sessionId) return;
      const sessionNotes = session.general.sessionNotes ?? '';
      const gameTimeEnd = session.general.gameTime?.toISOString() ?? new Date().toISOString();
      try {
        // The API returns the full, updated state on success
        const response = await api(`/api/sessions/${sessionId}/end`, { method: 'PUT', body: JSON.stringify({ gameTimeEnd, notes: sessionNotes }) });
        const latestState = await response.json() as SessionState;
        get().hydrateState(latestState);
        get().actions.addLogEntry('Session ended.');
      } catch (error) {
        logger.error(error, 'Error ending session');
      }
    },
  }
});